import {
    ArrayShape,
    CallExpressionShape,
    type Compiler,
    type Diagnostics,
    type Factory,
    ObjectShape,
    type Shape,
} from '@servicenow/sdk-build-core'
import type { Record as SdkRecord, Transform } from '@servicenow/sdk-build-core'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import { TRIGGER_INSTANCE_CALLEE_NAMES, PARENT_RECORD_MAPPER_KEY } from '../constants/playbook-plugin-constants'
import { TriggerInstanceShape } from '../utils/playbook-shapes'
import {
    PlaybookTriggerInstancePlugin,
    triggerInstanceRecordToShape,
} from '../plugins/playbook-trigger-instance-plugin'
import { PlaybookTriggerInstanceDiagnosticsPlugin } from '../plugins/playbook-trigger-instance-diagnostics-plugin'
import {
    parseMapperCallback,
    emitMissingRequiredInputDiagnostics,
    type ValidInput,
    type MapperValue,
} from './playbook-inputs-mapper-transform'

/**
 * Process a triggers object (PlaybookDefinition arg 2) into records.
 *
 * The 2nd arg is an object `{ triggers: [...] }` containing the triggers array.
 * Extracts the array from the `triggers` property, iterates each trigger
 * call expression, injects the parent foreign key (process_definition), and
 * delegates to PlaybookTriggerInstancePlugin for record creation.
 */
export async function processTriggersArrayToRecord({
    triggersObject,
    parentRecord,
    parentTable,
    parentRecordInputRecord,
    playbookInputRecords,
    compiler,
    rootDir,
    diagnostics,
    transform,
    factory,
}: {
    triggersObject: Shape
    parentRecord: SdkRecord
    parentTable: string | undefined
    parentRecordInputRecord?: SdkRecord | undefined
    playbookInputRecords?: SdkRecord[] | undefined
    compiler: Compiler
    rootDir: string
    diagnostics: Diagnostics
    transform: Transform
    factory: Factory
}): Promise<SdkRecord[]> {
    if (triggersObject.isUndefined()) {
        return []
    }

    // 2nd arg should be an object `{ triggers: [...PlaybookTrigger(...)] }`.
    // Extract the triggers array from the object.
    if (!triggersObject.is(ObjectShape)) {
        return []
    }
    const obj = triggersObject.as(ObjectShape)
    const triggersValue = obj.get('triggers')
    if (!triggersValue || !triggersValue.is(ArrayShape)) {
        return []
    }

    // Build a map of valid playbook input element names → mandatory flag.
    // The parent_record auto-generated input is excluded — it isn't a user-
    // declared input and is mapped via `parentRecord` in the mapper callback
    // (special-cased in parseMapperCallback), not as a regular declared input.
    const validInputs = new Map<string, ValidInput>()
    // Parallel map: element name → sys_pd_process_input sys_id. The trigger
    // instance plugin needs these sys_ids to create sys_variable_value records
    // for hardcoded mapper values.
    const playbookInputSysIds = new Map<string, string>()
    if (playbookInputRecords) {
        for (const r of playbookInputRecords) {
            const elementName = r.get('element')?.ifString()?.getValue()
            if (!elementName) {
                continue
            }
            const mandatory = Boolean(r.get('mandatory')?.toBoolean()?.getValue())
            validInputs.set(elementName, { mandatory })
            const sysId = r.getId().getValue()
            if (sysId) {
                playbookInputSysIds.set(elementName, sysId)
            }
        }
    }

    // Triggers is an array of PlaybookTrigger() / wfa.playbook.trigger() calls
    const triggersArray = triggersValue.as(ArrayShape)
    const elements = triggersArray.getElements()
    const records: SdkRecord[] = []

    for (const element of elements) {
        if (!element.is(CallExpressionShape)) {
            continue
        }

        const callExpr = element.as(CallExpressionShape)
        if (!TRIGGER_INSTANCE_CALLEE_NAMES.includes(callExpr.getCallee())) {
            continue
        }

        // Extract the 3 arguments: triggerType, config, triggerInputs.
        const triggerType = callExpr.getArgument(0)
        const configArg = callExpr.getArgument(1)?.asObject()
        const triggerInputsArg = callExpr.getArgument(2)?.asObject()

        if (!configArg || !triggerInputsArg) {
            continue
        }

        // Inject parent foreign key (processDefinition) into config.
        const configWithParent = configArg.merge({
            processDefinition: parentRecord.getId().getValue(),
        })

        const triggerTable = triggerInputsArg.get('table')?.ifString()?.getValue() ?? ''

        // Parse the optional 4th argument: (trigger) => ({ ... }) — the mapper.
        let mapperMappings: Map<string, MapperValue> | undefined
        const mapperArg = callExpr.getArgument(3)
        if (mapperArg && !mapperArg.isUndefined() && mapperArg.is(ArrowFunctionShape)) {
            const mapperArrow = mapperArg.as(ArrowFunctionShape)
            mapperMappings = await parseMapperCallback({
                mapperArrow,
                tableName: triggerTable,
                validInputs,
                compiler,
                rootDir,
                diagnostics,
                factory,
            })
        } else {
            // No mapper provided — every mandatory input is unmapped.
            emitMissingRequiredInputDiagnostics(callExpr, validInputs, diagnostics)
        }

        // Cross-table parentRecord check: when the trigger fires on a table other than the playbook's parentTable,
        // the platform needs `parentRecord` to identify which record the playbook should attach to. Check the mapper
        // (parsed above) because parentRecord must appear there.
        if (parentTable && triggerTable && triggerTable !== parentTable) {
            if (!mapperMappings?.has(PARENT_RECORD_MAPPER_KEY)) {
                diagnostics.error(
                    callExpr,
                    `parentRecord is required when the trigger table ("${triggerTable}") differs ` +
                        `from the playbook parentTable ("${parentTable}"). Add parentRecord to the ` +
                        `trigger's mapper callback ` +
                        ` (e.g. (trigger) => ({ parentRecord: wfa.playbook.dataPill(trigger.current.record) })).`
                )
            }
            if (mapperMappings?.get(PARENT_RECORD_MAPPER_KEY)?.value === '{{triggerRecord}}') {
                diagnostics.error(
                    callExpr,
                    'parentRecord should not be the trigger record when the trigger table differs from the ' +
                        'playbook parentTable.'
                )
            }
        }

        // parentRecord should not be specified if trigger fires on a table matching the playbook's parentTable
        // A default of {{triggerRecord}} is provided if no parentRecord is specified
        if (
            parentTable &&
            triggerTable &&
            triggerTable === parentTable &&
            mapperMappings?.has(PARENT_RECORD_MAPPER_KEY)
        ) {
            diagnostics.error(
                callExpr,
                `parentRecord should not be specified when the trigger table ("${triggerTable}") ` +
                    `matches the playbook parentTable ("${parentTable}").`
            )
        }

        const shape = new TriggerInstanceShape({
            source: callExpr,
            triggerType,
            config: configWithParent,
            triggerInputs: triggerInputsArg,
            parentRecordInputSysId: parentRecordInputRecord?.getId().getValue(),
            mapperMappings,
            playbookInputSysIds,
            isSameTableAsParent: !!parentTable && !!triggerTable && triggerTable === parentTable,
        })

        await transform.toRecord(shape, PlaybookTriggerInstanceDiagnosticsPlugin)
        const result = await transform.toRecord(shape, PlaybookTriggerInstancePlugin)
        if (result.success) {
            records.push(result.value)
        }
    }

    return records
}

/**
 * Build an ArrayShape of trigger call expressions from descendant records (XML → Fluent).
 *
 * Takes trigger instance records and their variable value descendants, transforms
 * each into a CallExpressionShape via triggerInstanceRecordToShape.
 */
export function buildTriggersArrayToShape({
    source,
    triggerRecords,
    descendants,
}: {
    source: SdkRecord
    triggerRecords: SdkRecord[]
    descendants: { query: (table: string) => SdkRecord[] }
}): ArrayShape | undefined {
    if (triggerRecords.length === 0) {
        return undefined
    }

    const triggerShapes: CallExpressionShape[] = []
    for (const record of triggerRecords) {
        const result = triggerInstanceRecordToShape(record, descendants)
        if (result.success) {
            triggerShapes.push(result.value)
        }
    }

    if (triggerShapes.length === 0) {
        return undefined
    }

    return new ArrayShape({
        source,
        elements: triggerShapes,
    })
}
